import type { Cell, Column, Row, Worksheet } from 'exceljs';
import { ASPHALT_TYPE_COLORS, WorkareaExcelColumn } from './export-excel-builder.constants';

export const styleHeader = (row: Row) =>
    row.eachCell(cell => {
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F0064' } };
    });

// ExcelJS has no autofit; capped so long text doesn't stretch the sheet
export const fitWidth = (column: Partial<Column>) => {
    let maxLength = 0;
    column.eachCell(cell => {
        const value: any = cell.value;
        const text =
            value instanceof Date ? 'dd/mm/yy hh:mm' : value?.formula ? '' : String(value?.text ?? value ?? '');
        maxLength = Math.max(maxLength, text.length);
    });
    column.width = Math.min(maxLength + 2, 50);
};

export const columnLetter = (sheet: Worksheet, columns: WorkareaExcelColumn[], key: string) =>
    sheet.getColumn(columns.findIndex(column => column.key === key) + 1).letter;

// Yes-values of the old export
export const isTruthy = (value: any) =>
    typeof value === 'boolean'
        ? value
        : ['1', 'true', 'ja', 'yes'].includes(
              String(value ?? '')
                  .trim()
                  .toLowerCase()
          );

// Numeric text → number; leading zero stays text (e.g. postcode/house number)
export const toCellValue = (value: any) =>
    typeof value === 'string' && /^-?\d+(\.\d+)?$/.test(value) && !/^-?0\d/.test(value) ? +value : value;

// Excel dates have no timezone: store Amsterdam clock time (summer/winter time)
export const toExcelDate = (value: string) => {
    if (!value) {
        return null;
    }
    const parts = Object.fromEntries(
        new Intl.DateTimeFormat('en-GB', {
            timeZone: 'Europe/Amsterdam',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hourCycle: 'h23',
        })
            .formatToParts(new Date(value))
            .map(part => [part.type, part.value])
    );
    return new Date(Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute, +parts.second));
};

// Black or white text, whichever reads better on the fill
export const fillCell = (cell: Cell, color: string) => {
    const [red, green, blue] = [0, 2, 4].map(index => parseInt(color.slice(index, index + 2), 16));
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${color}` } };
    cell.font = {
        ...cell.font,
        color: { argb: 0.299 * red + 0.587 * green + 0.114 * blue < 140 ? 'FFFFFFFF' : 'FF000000' },
    };
};

export const asphaltTypeColor = (asphaltType: string) =>
    ASPHALT_TYPE_COLORS[asphaltType] ??
    (/rood/i.test(asphaltType)
        ? 'E06666'
        : /beige/i.test(asphaltType)
          ? 'F9CB9C'
          : /frezen/i.test(asphaltType)
            ? 'D9D9D9'
            : 'B7B7B7');

// Export order: normal, rood, beige/frezen; by name within a group
export const byAsphaltTypeOrder = (a: string, b: string) => {
    const order = (asphaltType: string) => (/rood/i.test(asphaltType) ? 1 : /beige|frezen/i.test(asphaltType) ? 2 : 0);
    return order(a) - order(b) || a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
};
